import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { getAllResignationsApi } from "api/services/resignations";
import { formatDateHandler2, toTitleCase } from "utils/common";

const STATUS_TONE = { pending: "warning", approved: "success", rejected: "danger" };

const Field = ({ label, children }) => (
  <div style={{ minWidth: 0 }}>
    <div style={{ fontSize: "11px", color: "#9ca3af", fontWeight: "500", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "2px" }}>
      {label}
    </div>
    <div style={{ fontSize: "13px", color: "#1f2937", fontWeight: "600" }}>{children || "-"}</div>
  </div>
);

// Resignation history for one employee, shown on their profile so HR no
// longer has to leave the page to check it. The list API is role-scoped,
// so a viewer only ever sees requests they are allowed to see. The card
// only appears once at least one request is known; an employee with no
// resignation on record gets no card at all (a failed fetch still shows,
// so a problem is never mistaken for "none").
export const EmployeeResignations = ({ userData, isMobile }) => {
  const [items, setItems] = useState([]);
  // Starts as loading so the empty state is never shown before the profile
  // (and therefore the employee id) has arrived.
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!userData?.id) return undefined;
    let ignore = false;
    setLoading(true);
    setError("");
    getAllResignationsApi({ company_user: userData.id, page_size: 20 })
      .then((response) => {
        // Asked for one employee, but never trust that that is what came
        // back: `?company_user=` is a filter the API is free to not have,
        // and django-filter drops a param it does not declare SILENTLY —
        // the response is then every resignation the viewer's role can
        // see, 200 OK, and this card would list the whole company under
        // one person's name. A super user (access_scope="all") sees the
        // most, so they see the worst of it. Re-checking the id here costs
        // nothing and makes the card correct against any backend.
        const rows = (response.data?.results || []).filter(
          (item) => String(item?.company_user?.id) === String(userData.id)
        );
        if (!ignore) setItems(rows);
      })
      .catch(() => {
        if (!ignore) setError("Could not load resignation details.");
      })
      .finally(() => {
        if (!ignore) setLoading(false);
      });
    return () => {
      ignore = true;
    };
  }, [userData?.id]);

  const latest = items[0];

  if (loading || (!error && items.length === 0)) return null;

  return (
    <div className="row g-2 mt-2" style={isMobile ? { marginLeft: "0", marginRight: "0" } : {}}>
    <div className="col-12 d-flex">
    <div
      className="card shadow-sm profile-box flex-fill"
      style={{
        borderRadius: "8px",
        border: "1px solid #e5e7eb",
        background: "linear-gradient(to bottom, #ffffff 0%, #fef2f2 100%)",
        marginLeft: isMobile ? "8px" : "0px",
        marginRight: isMobile ? "8px" : "0px",
        marginBottom: isMobile ? "12px" : "0",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
      }}
    >
      <div className="card-body" style={{ padding: isMobile ? "12px" : "16px" }}>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h3 className="card-title mb-0" style={{ fontSize: isMobile ? "15px" : "16px", fontWeight: "600", color: "#1f2937" }}>
            Resignation
          </h3>
          {latest && (
            <span className={`badge badge-soft-${STATUS_TONE[latest.approval_status] || "secondary"}`}>
              {toTitleCase(latest.approval_status || "")}
            </span>
          )}
        </div>

        {error ? (
          <div role="alert" style={{ fontSize: "13px", color: "#b91c1c" }}>{error}</div>
        ) : (
          <div className="d-flex flex-column" style={{ gap: "10px" }}>
            {items.map((item) => (
              <div
                key={item.id}
                style={{ background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: "6px", padding: "12px" }}
              >
                <div className="d-flex justify-content-between align-items-center flex-wrap" style={{ gap: "8px", marginBottom: "10px" }}>
                  <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                    <span className={`badge badge-soft-${STATUS_TONE[item.approval_status] || "secondary"}`}>
                      {toTitleCase(item.approval_status || "")}
                    </span>
                    <span style={{ fontSize: "12px", color: "#6b7280" }}>
                      Applied on {formatDateHandler2(item.created_at)}
                    </span>
                  </div>
                  <Link
                    to={`/resignations/${item.id}`}
                    style={{ fontSize: "12px", fontWeight: "600", color: "rgb(211, 0, 26)", textDecoration: "none", whiteSpace: "nowrap" }}
                  >
                    View request <i className="las la-arrow-right" aria-hidden="true" />
                  </Link>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(auto-fit, minmax(150px, 1fr))",
                    gap: "10px",
                    marginBottom: "10px",
                  }}
                >
                  <Field label="Last working day">{formatDateHandler2(item.last_working_day)}</Field>
                  {item.approval_status === "pending" && (
                    <Field label="Awaiting">
                      {item.current_approver
                        ? `${item.current_approver.first_name || ""} ${item.current_approver.last_name || ""}`.trim()
                        : "-"}
                    </Field>
                  )}
                  {item.approval_status === "approved" && (
                    <Field label="Approved on">{formatDateHandler2(item.approved_on)}</Field>
                  )}
                </div>
                {item.reason && (
                  <Field label="Reason">
                    <span style={{ fontWeight: "500", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{item.reason}</span>
                  </Field>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
    </div>
    </div>
  );
};

export default EmployeeResignations;
